/**
 * Audit Logs component for viewing admin actions
 */
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { useTheme } from '../../context/ThemeContext';
import '../../styles/themes.css';

const AuditLogs = () => {
  const { loadAuditLogs } = useAdmin();
  const { theme } = useTheme();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [filters, setFilters] = useState({
    actionType: '',
    targetType: '',
    startDate: '',
    endDate: ''
  });

  const limit = 20;

  useEffect(() => {
    loadLogsData();
  }, [page, filters]);

  const loadLogsData = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page,
        limit,
        ...(filters.actionType && { action_type: filters.actionType }),
        ...(filters.targetType && { target_type: filters.targetType }),
        ...(filters.startDate && { start_date: filters.startDate }),
        ...(filters.endDate && { end_date: filters.endDate })
      };

      const response = await loadAuditLogs(params);
      setLogs(response.logs);
      setTotalLogs(response.total);
    } catch (err) {
      setError('Failed to load audit logs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      actionType: '',
      targetType: '',
      startDate: '',
      endDate: ''
    });
    setPage(1);
  };

  const getActionTypeColor = (actionType) => {
    switch (actionType) {
      case 'CREATE': return 'var(--color-success)';
      case 'UPDATE': return 'var(--color-warning)';
      case 'DELETE': return 'var(--color-error)';
      case 'BULK_UPDATE': return 'var(--color-accent)';
      default: return 'var(--color-outline)';
    }
  };

  const getTargetTypeColor = (targetType) => {
    switch (targetType) {
      case 'USER': return 'var(--color-primary)';
      case 'TASK': return 'var(--color-success)';
      case 'SUBMISSION': return 'var(--color-warning)';
      default: return 'var(--color-outline)';
    }
  };

  const formatChanges = (changes) => {
    if (!changes) return 'No details';

    try {
      const parsed = JSON.parse(changes);
      return Object.entries(parsed)
        .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
        .join(', ');
    } catch {
      return changes;
    }
  };

  const totalPages = Math.ceil(totalLogs / limit);

  return (
    <div className="themed-background" style={{ padding: '1rem', minHeight: '100vh' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <h2 style={{ color: 'var(--color-background-text)' }}>Audit Logs ({totalLogs})</h2>
      </div>

      {/* Filters */}
      <div className="themed-surface" style={{
        padding: '1rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px var(--shadow-color)',
        marginBottom: '1rem'
      }}>
        <h3 style={{ marginBottom: '1rem', color: 'var(--color-surface-text)' }}>Filters</h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: 'var(--color-surface-text)'
            }}>
              Action Type
            </label>
            <select
              name="actionType"
              value={filters.actionType}
              onChange={handleFilterChange}
              className="themed-input"
              style={{
                width: '100%',
                padding: '0.5rem'
              }}
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="BULK_UPDATE">Bulk Update</option>
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: 'var(--color-surface-text)'
            }}>
              Target Type
            </label>
            <select
              name="targetType"
              value={filters.targetType}
              onChange={handleFilterChange}
              className="themed-input"
              style={{
                width: '100%',
                padding: '0.5rem'
              }}
            >
              <option value="">All Targets</option>
              <option value="USER">Users</option>
              <option value="TASK">Tasks</option>
              <option value="SUBMISSION">Submissions</option>
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: 'var(--color-surface-text)'
            }}>
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="themed-input"
              style={{
                width: '100%',
                padding: '0.5rem'
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: 'var(--color-surface-text)'
            }}>
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="themed-input"
              style={{
                width: '100%',
                padding: '0.5rem'
              }}
            />
          </div>
        </div>

        <button
          onClick={clearFilters}
          className="themed-button"
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'var(--color-outline)'
          }}
        >
          Clear Filters
        </button>
      </div>

      {error && (
        <div className="status-error themed-surface" style={{
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid var(--color-error)'
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: 'var(--color-background-text)'
        }}>
          Loading audit logs...
        </div>
      ) : (
        <>
          {/* Audit Logs Table */}
          <div className="themed-surface" style={{
            borderRadius: '8px',
            boxShadow: '0 2px 4px var(--shadow-color)',
            overflow: 'hidden'
          }}>
            <table className="themed-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Changes</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td>
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: 'var(--color-outline)',
                        color: 'var(--color-primary-text)',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}>
                        {log.admin_email || log.admin_id}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        backgroundColor: getActionTypeColor(log.action_type),
                        color: 'var(--color-primary-text)',
                        fontWeight: 'bold'
                      }}>
                        {log.action_type}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          backgroundColor: getTargetTypeColor(log.target_type),
                          color: 'var(--color-primary-text)',
                          fontWeight: 'bold',
                          width: 'fit-content'
                        }}>
                          {log.target_type}
                        </span>
                        <small style={{ color: 'var(--color-surface-text)', opacity: 0.7 }}>
                          ID: {log.target_id}
                        </small>
                      </div>
                    </td>
                    <td style={{
                      maxWidth: '300px',
                      wordBreak: 'break-word'
                    }}>
                      <details>
                        <summary style={{ cursor: 'pointer', color: 'var(--color-accent)' }}>
                          View Changes
                        </summary>
                        <div style={{
                          marginTop: '0.5rem',
                          padding: '0.5rem',
                          backgroundColor: 'var(--color-background)',
                          color: 'var(--color-background-text)',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                          border: '1px solid var(--border-color)'
                        }}>
                          {formatChanges(log.changes_made)}
                        </div>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {logs.length === 0 && (
            <div className="themed-surface" style={{
              textAlign: 'center',
              padding: '3rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px var(--shadow-color)'
            }}>
              <h3 style={{ color: 'var(--color-surface-text)' }}>No Audit Logs Found</h3>
              <p style={{
                color: 'var(--color-surface-text)',
                opacity: 0.7
              }}>
                No admin actions match the current filters.
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: '2rem'
            }}>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="themed-button"
                style={{
                  padding: '0.5rem 1rem',
                  opacity: page === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>

              <span className="themed-surface" style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                border: '1px solid var(--border-color)'
              }}>
                Page {page} of {totalPages}
              </span>

              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="themed-button"
                style={{
                  padding: '0.5rem 1rem',
                  opacity: page === totalPages ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLogs;